import { addMonths, formatMonth, useSnackbar } from '@hue/design-system';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { commissionsApi, financialClosingApi, getApiError, guidesApi } from '../../lib/api.js';
import { accountingApi, closingApi } from '../../modules/accounting/index.js';
import { useCompanyScope } from '../company-context.js';
import { companySections } from '../company-sections.js';
import {
  monthBounds,
  useOperationalPeriod,
  type OperationalMonth,
} from '../operational-period-context.js';
import { GuidesContext } from './guide-context.js';
import { GuideDock } from './guide-dock.js';
import { GuideModal } from './guide-modal.js';
import {
  firstPending,
  guideVisible,
  nextPending,
  stepDone,
  stepState,
  stepsContracted,
  type StepState,
} from './guide-model.js';
import type { GuideDefinition, GuideFacts, GuideStep } from './guide-types.js';
import { monthClosingGuide } from './month-closing-guide.js';

/// A cada 15 segundos enquanto o roteiro está aberto ou recolhido: a pessoa trabalha nas outras
/// telas, e o passo medido precisa se concluir sozinho sem ela recarregar nada.
const REFRESH_MS = 15_000;

const guides: readonly GuideDefinition[] = [monthClosingGuide];

export function GuidesProvider({ children }: { children: ReactNode }) {
  const { company } = useCompanyScope();
  const contracted = useMemo(
    () => ({ moduleKeys: company.moduleKeys, featureKeys: company.featureKeys }),
    [company.moduleKeys, company.featureKeys],
  );
  const available = useMemo(
    () => guides.filter((guide) => guideVisible(guide, contracted)),
    [contracted],
  );
  const [session, setSession] = useState<{ guide: GuideDefinition; id: number } | null>(null);
  const open = useCallback((guide: GuideDefinition) => {
    setSession((current) =>
      current?.guide.key === guide.key ? { guide, id: current.id + 1 } : { guide, id: Date.now() },
    );
  }, []);
  const value = useMemo(() => ({ available, open }), [available, open]);
  return (
    <GuidesContext.Provider value={value}>
      {children}
      {session && guideVisible(session.guide, contracted) ? (
        <GuideSession
          /* Trocar de unidade troca o progresso inteiro: a sessão recomeça do passo pendente. */
          key={`${session.guide.key}:${company.activeBusinessUnitId ?? 'all'}`}
          guide={session.guide}
          reopen={session.id}
          onExit={() => setSession(null)}
        />
      ) : null}
    </GuidesContext.Provider>
  );
}

function useGuideFacts(month: string): { facts: GuideFacts; loading: boolean } {
  const { company, can, today } = useCompanyScope();
  const unit = company.activeBusinessUnitId ?? null;
  const accounting = useQuery({
    queryKey: ['accounting', 'closing', 'month', month, today, company.id, unit],
    queryFn: () => closingApi.month(month, today),
    enabled:
      unit !== null && company.moduleKeys.includes('accounting') && can('accounting.reports.read'),
    refetchInterval: REFRESH_MS,
    retry: false,
    refetchOnMount: 'always',
  });
  const cash = useQuery({
    queryKey: ['guides', 'financial-closing', month, company.id, unit],
    queryFn: () => financialClosingApi.get(month),
    enabled: unit !== null && can('financial_closing.read'),
    refetchInterval: REFRESH_MS,
    retry: false,
    refetchOnMount: 'always',
  });
  const commissions = useQuery({
    queryKey: ['guides', 'commissions', month, company.id, unit],
    queryFn: () => commissionsApi.closing(month),
    enabled:
      unit !== null &&
      company.featureKeys.includes('sales.commissions') &&
      can('sales.commissions.read'),
    refetchInterval: REFRESH_MS,
    retry: false,
  });
  const classifications = useQuery({
    queryKey: ['guides', 'classifications', month, company.id, unit],
    queryFn: async () => {
      const bounds = monthBounds(month as OperationalMonth);
      const pages = await Promise.all(
        (['MOVEMENTS', 'OBLIGATIONS'] as const).map((kind) =>
          accountingApi.queue({
            kind,
            from: bounds.from!,
            to: bounds.to!,
            filter: 'PENDING',
            sort: 'date',
            direction: 'asc',
            limit: 1,
            offset: 0,
          }),
        ),
      );
      return {
        count: pages.reduce((sum, page) => sum + page.summary.pendingCount, 0),
        cents: pages.reduce((sum, page) => sum + page.summary.pendingCents, 0),
      };
    },
    enabled:
      unit !== null && company.moduleKeys.includes('accounting') && can('accounting.classify'),
    refetchInterval: REFRESH_MS,
    retry: false,
  });
  const facts = useMemo<GuideFacts>(() => {
    const view = accounting.isError ? undefined : accounting.data;
    const closing = cash.isError ? undefined : cash.data;
    const cashClosed = closing ? closing.status === 'CLOSED' : view?.cashClosed;
    return {
      ...(view
        ? {
            accountingStatus: view.status,
          }
        : {}),
      ...(closing ? { movementsWithoutTreatment: closing.blockerCount } : {}),
      ...(!commissions.isError && commissions.data
        ? {
            pendingCommissionsCents: commissions.data.lines.reduce(
              (sum, line) => sum + line.pendingCommissionCents,
              0,
            ),
          }
        : {}),
      ...(!classifications.isError && classifications.data
        ? {
            unclassifiedCount: classifications.data.count,
            noAccountCents: classifications.data.cents,
          }
        : {}),
      ...(cashClosed === undefined ? {} : { cashClosed }),
    };
  }, [
    accounting.data,
    accounting.isError,
    cash.data,
    cash.isError,
    commissions.data,
    commissions.isError,
    classifications.data,
    classifications.isError,
  ]);
  return {
    facts,
    loading:
      accounting.isLoading || cash.isLoading || commissions.isLoading || classifications.isLoading,
  };
}

function GuideSession({
  guide,
  reopen,
  onExit,
}: {
  guide: GuideDefinition;
  reopen: number;
  onExit: () => void;
}) {
  const { company, can, today } = useCompanyScope();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const period = useOperationalPeriod(today);
  const month = period.month === 'ALL' ? addMonths(today, -1).slice(0, 7) : period.month;
  const setMonth = (next: string) => period.setMonth(next as OperationalMonth);
  const [chosen, setChosen] = useState<{ month: string; key: string } | null>(null);
  const [minimized, setMinimized] = useState(false);
  const [seenReopen, setSeenReopen] = useState(reopen);
  if (seenReopen !== reopen) {
    setSeenReopen(reopen);
    setMinimized(false);
  }

  const unitId = company.activeBusinessUnitId ?? null;
  const steps = useMemo(
    () =>
      stepsContracted(guide, { moduleKeys: company.moduleKeys, featureKeys: company.featureKeys }),
    [guide, company.moduleKeys, company.featureKeys],
  );
  const marksKey = ['guides', guide.key, 'marks', month, unitId, company.id];
  const marks = useQuery({
    queryKey: marksKey,
    queryFn: () => guidesApi.marks(guide.key, month),
    enabled: unitId !== null,
    refetchInterval: REFRESH_MS,
    retry: false,
  });
  const { facts, loading } = useGuideFacts(month);
  /// Entra no meio das frases: "as vendas de outubro de 2026".
  const monthName = formatMonth(month).toLocaleLowerCase('pt-BR');
  const states: StepState[] = steps.map((step) =>
    stepState(
      step,
      facts,
      (marks.isError ? undefined : marks.data)?.find((mark) => mark.stepKey === step.key),
      monthName,
    ),
  );
  const counted = steps.filter((step) => !step.appendix);
  const done = states.filter((state, i) => !steps[i]!.appendix && stepDone(state)).length;

  const settled = !loading && !marks.isLoading;
  const chosenIndex =
    chosen?.month === month ? steps.findIndex((step) => step.key === chosen.key) : -1;
  const index = chosenIndex >= 0 ? chosenIndex : settled ? firstPending(states, steps) : 0;
  const step = steps[index]!;
  const goTo = (target: number) => {
    setChosen({ month, key: steps[target]!.key });
  };

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['guides', guide.key, 'marks'] });
  const unmark = useMutation({
    mutationFn: (stepKey: string) => guidesApi.unmark(guide.key, { month, stepKey }),
    onSuccess: refresh,
    onError: (error) => snackbar.failure(getApiError(error)),
  });
  const mark = useMutation({
    mutationFn: (input: { stepKey: string; status: 'CONFIRMED' | 'NOT_APPLICABLE' }) =>
      guidesApi.mark(guide.key, { month, ...input }),
    onSuccess: async (_, input) => {
      snackbar.success(
        input.status === 'CONFIRMED' ? 'Confirmação registrada.' : 'Marcado como não se aplica.',
      );
      await refresh();
      goTo(nextPending(index, states, steps));
    },
    onError: (error) => snackbar.failure(getApiError(error)),
  });

  const canReach = (target: GuideStep) => {
    const section = companySections.find((one) => one.key === target.target.screen);
    return section ? section.requires.some((one) => can(one)) : false;
  };
  const goToScreen = (target: GuideStep) => {
    const section = companySections.find((one) => one.key === target.target.screen)!;
    setMonth(month);
    setMinimized(true);
    void navigate({
      to: section.to,
      params: { companyKey: company.key },
      ...(target.target.search ? { search: target.target.search } : {}),
    } as never);
  };
  const goToHelp = (target: GuideStep) => {
    setMinimized(true);
    void navigate({
      to: '/companies/$companyKey/documentation',
      params: { companyKey: company.key },
      search: { from: target.help },
    });
  };

  if (minimized) {
    return (
      <GuideDock
        guideTitle={guide.title}
        step={step}
        state={states[index]!}
        position={counted.indexOf(step) + 1}
        total={counted.length}
        hasNext={index < steps.length - 1}
        onReopen={() => setMinimized(false)}
        onNext={() => {
          goTo(nextPending(index, states, steps));
          setMinimized(false);
        }}
      />
    );
  }
  const unit = company.businessUnits?.find((one) => one.id === unitId);
  return (
    <GuideModal
      guide={guide}
      steps={steps}
      states={states}
      index={index}
      month={month}
      monthName={monthName}
      lastMonth={today.slice(0, 7)}
      done={done}
      total={counted.length}
      unitName={unit?.displayName ?? null}
      needsUnit={unitId === null}
      canConfirm={can('guides.steps.confirm')}
      progressAvailable={!marks.isPending && !marks.isError}
      loadError={
        marks.isError
          ? 'Não foi possível consultar as confirmações deste mês. Você pode ler o roteiro, mas confirme novamente quando a consulta voltar.'
          : null
      }
      busy={mark.isPending || unmark.isPending}
      canReach={canReach}
      onMonth={(next) => {
        setMonth(next);
        setChosen(null);
      }}
      onStep={goTo}
      onClose={onExit}
      onGo={goToScreen}
      onHelp={goToHelp}
      onConfirm={(status) => mark.mutate({ stepKey: step.key, status })}
      onUndo={() => unmark.mutate(step.key)}
    />
  );
}
